import { useState } from 'react';
import { KeyRound, ShieldCheck, Building2, Mail, Lock, CheckCircle2, Check, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/ToastProvider';
import { changePassword } from '../api/authApi';

const SECURITY_TIPS = ["Use a password you don't reuse elsewhere", 'Change your password periodically', 'Never share your login with staff — invite them separately instead'];

const PW_RULES = [
  { id: 'length', label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { id: 'upper', label: 'At least 1 uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { id: 'lower', label: 'At least 1 lowercase letter', test: (p) => /[a-z]/.test(p) },
  { id: 'digit', label: 'At least 1 digit', test: (p) => /\d/.test(p) },
  { id: 'special', label: 'At least 1 special char (@$!%*?&_#)', test: (p) => /[@$!%*?&_#]/.test(p) },
];

function ChangePasswordSection() {
  const { showToast } = useToast();
  const [fields, setFields] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [loading, setLoading] = useState(false);

  const rules = PW_RULES.map((r) => ({ ...r, met: r.test(fields.newPassword) }));
  const allMet = rules.every((r) => r.met);
  const sameAsCurrent = fields.currentPassword && fields.newPassword && fields.currentPassword === fields.newPassword;
  const mismatch = fields.newPassword && fields.confirmPassword && fields.newPassword !== fields.confirmPassword;
  const isValid = fields.currentPassword.length > 0 && allMet && fields.newPassword === fields.confirmPassword && !sameAsCurrent;

  function handleChange(e) {
    setFields((f) => ({ ...f, [e.target.name]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!isValid) return;
    setLoading(true);
    try {
      await changePassword(fields);
      showToast('Password changed successfully.');
      setFields({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      showToast(err.response?.data?.message || 'Something went wrong', 'error');
    } finally {
      setLoading(false);
    }
  }

  const pwFields = [
    { name: 'currentPassword', label: 'Current Password', autoComplete: 'current-password' },
    { name: 'newPassword', label: 'New Password', autoComplete: 'new-password' },
    { name: 'confirmPassword', label: 'Confirm New Password', autoComplete: 'new-password' },
  ];

  return (
    <div className="google-card p-6 bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043]">
      <div className="flex items-center gap-2 pb-4 border-b border-[#e8eaed] dark:border-[#3c4043] mb-4">
        <KeyRound className="w-5 h-5 text-[#1a73e8]" />
        <h3 className="font-semibold text-base text-[#202124] dark:text-[#e8eaed]">Change Password</h3>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {pwFields.map(({ name, label, autoComplete }) => (
            <div key={name}>
              <label className="text-xs font-semibold text-[#5f6368] dark:text-[#9aa0a6] block mb-1.5">{label}</label>
              <div className="google-input-box px-3.5 py-2.5">
                <input type="password" name={name} value={fields[name]} onChange={handleChange} autoComplete={autoComplete}
                  placeholder="••••••••" className="w-full bg-transparent text-sm font-medium outline-none text-[#202124] dark:text-[#e8eaed]" />
              </div>
            </div>
          ))}
        </div>

        {fields.newPassword && (
          <div className="rounded-xl p-3 space-y-1.5 bg-[#f1f3f4] dark:bg-[#202124] border border-[#dadce0] dark:border-[#3c4043]">
            {rules.map((r) => (
              <div key={r.id} className="flex items-center gap-2">
                {r.met ? <Check className="w-3.5 h-3.5 shrink-0 text-[#1e8e3e]" /> : <X className="w-3.5 h-3.5 shrink-0 text-[#9aa0a6]" />}
                <span className={`text-xs ${r.met ? 'text-[#202124] dark:text-[#e8eaed]' : 'text-[#9aa0a6]'}`}>{r.label}</span>
              </div>
            ))}
            {sameAsCurrent && (
              <div className="flex items-center gap-2">
                <X className="w-3.5 h-3.5 shrink-0 text-[#d93025]" />
                <span className="text-xs text-[#9aa0a6]">Must differ from current password</span>
              </div>
            )}
            {mismatch && (
              <div className="flex items-center gap-2">
                <X className="w-3.5 h-3.5 shrink-0 text-[#d93025]" />
                <span className="text-xs text-[#9aa0a6]">Passwords do not match</span>
              </div>
            )}
          </div>
        )}

        <button type="submit" disabled={!isValid || loading}
          className="px-6 py-2.5 rounded-full text-xs font-semibold bg-[#1a73e8] hover:bg-[#1557b0] text-white shadow-xs cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
          {loading ? 'Changing Password...' : 'Change Password'}
        </button>
      </form>
    </div>
  );
}

function CustomerAccessCallout() {
  const { user, addRole } = useAuth();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const isCustomer = user?.roles?.includes('customer');

  async function handleClick() {
    setLoading(true);
    try {
      await addRole('customer');
      showToast('You can now log in to the main Upnoraa app with this account.');
    } catch (err) {
      showToast(err.response?.data?.message || 'Something went wrong', 'error');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="p-6 rounded-2xl bg-gradient-to-br from-[#e8f0fe] to-[#d2e3fc] dark:from-[#1a2e4c] dark:to-[#17253b] border border-blue-200 dark:border-blue-800">
      <div className="flex items-start gap-3">
        <div className="p-2.5 rounded-2xl bg-white dark:bg-[#303134] text-[#1a73e8] shadow-xs shrink-0">
          <ShieldCheck className="w-5 h-5" />
        </div>
        <div className="text-xs text-[#3c4043] dark:text-[#dadce0] leading-relaxed flex-1">
          <h4 className="font-bold text-sm text-[#1a73e8] dark:text-[#8ab4f8] mb-1">Customer Access</h4>
          {isCustomer ? (
            <p>This account already has customer access — sign in with the same email and password in the main Upnoraa app to manage personal bookings.</p>
          ) : (
            <>
              <p>Add customer access to this account so you can also use the main Upnoraa app (workouts, nutrition, bookings) with the same login.</p>
              <button onClick={handleClick} disabled={loading}
                className="mt-3 px-4 py-2 rounded-full text-xs font-semibold bg-[#1a73e8] hover:bg-[#1557b0] text-white cursor-pointer disabled:opacity-60">
                {loading ? 'Adding...' : 'Become a Customer'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ProfilePage() {
  const { user } = useAuth();
  const initial = (user?.name || '?').charAt(0).toUpperCase();

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-[#202124] dark:text-[#e8eaed]">Profile</h2>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        <div className="lg:col-span-8 space-y-4">
          <div className="google-card p-5 bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043] flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-tr from-[#1a73e8] to-[#8ab4f8] text-white flex items-center justify-center font-bold text-xl shrink-0">
              {initial}
            </div>
            <div>
              <div className="font-semibold text-[#202124] dark:text-[#e8eaed]">{user?.name}</div>
              <span className="text-xs text-[#5f6368] dark:text-[#9aa0a6]">{user?.email}</span>
            </div>
          </div>

          <ChangePasswordSection />
          <CustomerAccessCallout />
        </div>

        <div className="lg:col-span-4 space-y-4">
          <div className="google-card p-5 bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043]">
            <h3 className="font-semibold text-sm text-[#202124] dark:text-[#e8eaed] mb-3">Account Details</h3>
            <div className="space-y-3 text-xs">
              <div className="flex items-center gap-2.5">
                <Building2 className="w-4 h-4 text-[#9aa0a6] shrink-0" />
                <div>
                  <span className="text-[#9aa0a6] block">Name</span>
                  <span className="text-[#202124] dark:text-[#e8eaed] font-medium">{user?.name}</span>
                </div>
              </div>
              <div className="flex items-center gap-2.5">
                <Mail className="w-4 h-4 text-[#9aa0a6] shrink-0" />
                <div>
                  <span className="text-[#9aa0a6] block">Email</span>
                  <span className="text-[#202124] dark:text-[#e8eaed] font-medium">{user?.email}</span>
                </div>
              </div>
              <div className="flex items-center gap-2.5">
                <Lock className="w-4 h-4 text-[#9aa0a6] shrink-0" />
                <div>
                  <span className="text-[#9aa0a6] block">Role</span>
                  <span className="text-[#202124] dark:text-[#e8eaed] font-medium">Gym Owner</span>
                </div>
              </div>
            </div>
          </div>

          <div className="google-card p-5 bg-white dark:bg-[#303134] border border-[#dadce0] dark:border-[#3c4043]">
            <h3 className="font-semibold text-sm text-[#202124] dark:text-[#e8eaed] mb-3">Security Tips</h3>
            <div className="space-y-2.5">
              {SECURITY_TIPS.map((tip) => (
                <div key={tip} className="flex items-start gap-2 text-xs text-[#5f6368] dark:text-[#9aa0a6]">
                  <CheckCircle2 className="w-3.5 h-3.5 text-[#34A853] shrink-0 mt-0.5" />
                  <span>{tip}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
